import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Container, VStack, Heading, Text, Box, Badge, HStack,
  OrderedList, ListItem, Divider, Button, Link,
} from "@chakra-ui/react";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";

const CalendarBroadcastUpgrade: React.FC = () => {
  return (
    <>
      <NavbarWithCallToAction />
      <Container maxW="container.md" py={10}>
        <VStack spacing={6} align="stretch">
          <Link as={RouterLink} to="/updates" _hover={{ textDecoration: "none" }}>
            <Button variant="ghost" size="sm" leftIcon={<FiArrowLeft />} color="gray.500">
              All Updates
            </Button>
          </Link>

          <VStack spacing={3} align="start">
            <HStack>
              <Badge colorScheme="blue">Calendar</Badge>
              <Badge colorScheme="purple">v1.4.0</Badge>
              <Text fontSize="xs" color="gray.400">14 Apr 2026</Text>
            </HStack>
            <Heading size="lg" color="#2c5282">Calendar Broadcasts: pick one client, attach an invoice, let AI write it</Heading>
            <Text color="gray.600">
              Three changes that make calendar broadcasts ready for real client work — no more workarounds, no more flicking between tabs to copy a bill link, and no more staring at a blank email box wondering what to write.
            </Text>
          </VStack>

          <Divider />

          <Box>
            <Heading size="md" color="#2b6cb0" mb={3}>What's New</Heading>
            <VStack spacing={3} align="start">
              <Text>
                <strong>1. Pick individual clients</strong> — the recipient panel now has a search box below the tag groups. Type a name, email, or phone number and tick the client. No need to invent a one-person tag any more.
              </Text>
              <Text>
                <strong>2. Attach an invoice</strong> — a new "Attach Invoice" dropdown lists your recent bills. Pick one, click <em>Insert into draft</em>, and the bill link gets merged into both the email body and the SMS automatically.
              </Text>
              <Text>
                <strong>3. AI Draft Helper</strong> — type a short goal in plain English (e.g. "Friendly reminder for tomorrow's 2pm session, mention the invoice"). AI drafts both the email and the SMS for you. Don't like it? Type feedback ("warmer tone", "shorter SMS"), click Refine. Repeat until you're happy. Click <em>Use this draft</em> to drop it into the form.
              </Text>
            </VStack>
          </Box>

          <Box>
            <Heading size="md" color="#2b6cb0" mb={3}>How to Use</Heading>
            <OrderedList spacing={3} pl={4}>
              <ListItem>Open any calendar and create a new event.</ListItem>
              <ListItem>Choose <strong>SMS Broadcast</strong>, <strong>Email Broadcast</strong>, or <strong>SMS + Email Broadcast</strong>.</ListItem>
              <ListItem>If you've created a bill for the client, pick it from the new <strong>Attach Invoice</strong> dropdown.</ListItem>
              <ListItem>Type a short goal in the <strong>AI Draft Helper</strong> ("Reminder for Jamie's session tomorrow 2pm — mention to bring water"), click <strong>Draft both</strong>.</ListItem>
              <ListItem>Tweak with feedback or click <strong>Use this draft</strong> to fill the email + SMS fields.</ListItem>
              <ListItem>Scroll to <strong>Choose Recipients</strong> and either tick a tag group <em>or</em> type a name in the new <strong>Or pick individual clients</strong> search box.</ListItem>
              <ListItem>Set the schedule and save. The broadcast cron job sends it at the scheduled time.</ListItem>
            </OrderedList>
          </Box>

          <Divider />

          <Box bg="green.50" p={5} borderRadius="md" border="1px solid" borderColor="green.200">
            <Heading size="md" color="#38a169" mb={3}>Try It Now!</Heading>
            <Text mb={3}>
              Open your calendar, pick a session, and try sending one client a reminder + invoice with the AI draft helper.
            </Text>
            <Link as={RouterLink} to="/calendars" _hover={{ textDecoration: "none" }}>
              <Button colorScheme="green" size="md" rightIcon={<FiArrowRight />}>
                Open My Calendars
              </Button>
            </Link>
          </Box>

          <Box>
            <Text>As always, reach out if anything's not behaving — happy to jump on a call.</Text>
            <Text mt={4}>
              Regards,<br />
              <strong>Dr Thomas Miller</strong><br />
              <Text as="em" color="gray.500">Business Builder</Text>
            </Text>
          </Box>
        </VStack>
      </Container>
      <FooterWithFourColumns />
    </>
  );
};

export default CalendarBroadcastUpgrade;
